// Peças compartilhadas pelas telas.

import { useEffect, useState, type CSSProperties, type ReactNode } from "react";

const ROTULO: Record<string, string> = {
  aguardando_validacao: "aguardando validação",
  validado: "validado",
  rejeitado: "rejeitado",
  publicado: "publicado",
  desativado: "desativado",
  ativa: "ativa",
  inativa: "inativa",
  sucesso: "sucesso",
  erro: "erro",
};

const CLASSE: Record<string, string> = {
  aguardando_validacao: "pilula-aguardando",
  validado: "pilula-validado",
  rejeitado: "pilula-erro",
  publicado: "pilula-publicado",
  desativado: "pilula-neutra",
  ingerido: "pilula-neutra",
  ativa: "pilula-validado",
  inativa: "pilula-neutra",
  sucesso: "pilula-publicado",
  erro: "pilula-erro",
  sim: "pilula-neutra",
};

export function Pilula({ status }: { status: string }): ReactNode {
  return (
    <span className={`pilula ${CLASSE[status] ?? "pilula-neutra"}`}>
      {ROTULO[status] ?? status}
    </span>
  );
}

/** Data em PT-BR com hora — a tela de curadoria vive de "quando foi isso?". */
export function quando(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Vazio({ children }: { children: ReactNode }): ReactNode {
  return <p className="vazio">{children}</p>;
}

export function Carregando(): ReactNode {
  return <p className="vazio">Carregando…</p>;
}

export function Falha({ erro }: { erro: unknown }): ReactNode {
  return (
    <div className="aviso aviso-erro" role="alert">
      <div>
        <b>Não foi possível carregar</b>
        <p>{erro instanceof Error ? erro.message : String(erro)}</p>
      </div>
    </div>
  );
}

/**
 * Gaveta lateral. `Escape` fecha e o foco vai para dentro ao abrir — a tela de
 * curadoria é operada por teclado, em sequências longas de validar/rejeitar.
 */
export function Gaveta({
  titulo,
  subtitulo,
  cabecalho,
  aoFechar,
  aoEscape,
  children,
}: {
  titulo: string;
  subtitulo?: ReactNode;
  /** Fica FIXO no topo: ações e abas não podem rolar junto com a tabela. */
  cabecalho?: ReactNode;
  aoFechar: () => void;
  /**
   * Chance de tratar o `Escape` antes de a gaveta fechar.
   *
   * Devolva `true` para dizer "eu resolvi, não feche". Existe por um caso
   * específico e caro: com uma seleção de 300 feições ativa, `Escape` deve
   * limpar a seleção, e só fechar a gaveta no segundo toque. Perder essa
   * seleção por um toque de tecla é o tipo de erro que faz a pessoa refazer meia
   * hora de trabalho.
   *
   * Só vale para a TECLA. O botão "Esc" e o clique no fundo continuam fechando
   * direto: quem clica em fechar está pedindo para fechar.
   */
  aoEscape?: () => boolean;
  children: ReactNode;
}): ReactNode {
  useEffect(() => {
    const t = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      if (aoEscape?.()) return;
      aoFechar();
    };
    document.addEventListener("keydown", t);
    return () => document.removeEventListener("keydown", t);
  }, [aoFechar, aoEscape]);

  return (
    <div className="gaveta-inv">
      <button className="gaveta-fundo" aria-label="Fechar" onClick={aoFechar} />
      <aside className="gaveta" role="dialog" aria-modal="true" aria-label={titulo}>
        <div className="gaveta-topo">
          <div className="gaveta-titulo">
            <div>
              <h2>{titulo}</h2>
              {subtitulo ? <div className="mono terciario">{subtitulo}</div> : null}
            </div>
            {/* O `Escape` também fecha — o botão existe para quem não sabe
                disso. A curadoria é operada por teclado em sequências longas. */}
            <button className="btn btn-pequeno" onClick={aoFechar}>
              Esc
            </button>
          </div>
          {cabecalho}
        </div>
        <div className="gaveta-corpo">{children}</div>
      </aside>
    </div>
  );
}

export type Torrada = { texto: string; erro?: boolean } | null;

/** Aviso flutuante. Some sozinho; erro fica mais tempo, porque se lê mais devagar. */
export function AvisoFlutuante({
  aviso,
  aoSumir,
}: {
  aviso: Torrada;
  aoSumir: () => void;
}): ReactNode {
  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(aoSumir, aviso.erro ? 7000 : 3500);
    return () => clearTimeout(t);
  }, [aviso, aoSumir]);

  if (!aviso) return null;
  return (
    <div className={`torrada ${aviso.erro ? "torrada-erro" : ""}`} role="status">
      {aviso.texto}
    </div>
  );
}

/**
 * Botão que se desabilita enquanto a ação corre.
 *
 * Não é enfeite: promover e executar ingestão demoram, e sem isto o curador
 * clica de novo achando que não pegou. A trava do banco recusa a segunda, mas
 * o que ele vê é um erro que não devia existir.
 */
export function BotaoAcao({
  aoClicar,
  children,
  className = "btn",
  desabilitado,
  rotuloCarregando = "…",
  style,
}: {
  aoClicar: () => Promise<void>;
  children: ReactNode;
  className?: string;
  desabilitado?: boolean;
  rotuloCarregando?: string;
  style?: CSSProperties;
}): ReactNode {
  const [correndo, setCorrendo] = useState(false);
  return (
    <button
      className={className}
      style={style}
      disabled={correndo || desabilitado}
      onClick={() => {
        setCorrendo(true);
        void aoClicar().finally(() => setCorrendo(false));
      }}
    >
      {correndo ? rotuloCarregando : children}
    </button>
  );
}
